import { useState } from "react";

export type Scope = readonly (string | null | undefined)[];

export interface ScopeGeneration {
  generation: number;
  /** The last value seen at each scope position, kept while that position is unknown. */
  known: Scope;
}

/**
 * Returns `previous` itself when `scope` tells it nothing new. A position resolving from
 * unknown to a value keeps the generation; a known value replaced by a different one bumps it.
 */
export const advanceScopeGeneration = (
  previous: ScopeGeneration,
  scope: Scope,
): ScopeGeneration => {
  let switched = false;
  const known = scope.map((value, index) => {
    const prior = previous.known[index];
    if (value == null) return prior;
    if (prior != null && prior !== value) switched = true;
    return value;
  });
  if (known.every((value, index) => value === previous.known[index])) return previous;
  return { generation: switched ? previous.generation + 1 : previous.generation, known };
};

/**
 * A React key that changes only when the scope moves from one known value to another.
 *
 * Keying a subtree directly on ids that resolve after the first render (route ids after
 * hydration, the user once the session loads) remounts the subtree when they resolve, which
 * repaints the whole page. This key stays put while they resolve.
 */
export const useScopeGeneration = (scope: Scope): string => {
  const [state, setState] = useState<ScopeGeneration>(() => ({ generation: 0, known: scope }));
  const next = advanceScopeGeneration(state, scope);
  if (next !== state) {
    setState(next);
  }
  return String(next.generation);
};
